import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico/menu`;

/** O erro fica local: sem o menu personalizado, o cabeçalho mostra o padrão, sem modal. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Um item de `GET publico/menu`. O dropdown vem com link nulo e os subitens dentro. */
export interface ItemMenuPublicoResponse {
  desRotuloImc: string;
  /** 'interno' (caminho do portal) ou 'externo' (URL com http/https); nulo no dropdown. */
  desDestinoImc: string | null;
  desLinkImc: string | null;
  bitNovaAbaImc: boolean;
  subitens: ItemMenuPublicoResponse[];
}

/** Um item do menu personalizado como o cabeçalho o desenha. */
export interface ItemMenuPortal {
  rotulo: string;
  /** Interno navega pelo `routerLink`; externo é `href`. */
  interno: boolean;
  /** Nulo no dropdown: quem navega são os subitens. */
  link: string | null;
  novaAba: boolean;
  subitens: readonly ItemMenuPortal[];
}

function paraLink(item: ItemMenuPublicoResponse): ItemMenuPortal | null {
  const rotulo = item.desRotuloImc?.trim();
  const link = item.desLinkImc?.trim();

  if (!rotulo || !link) return null;

  const externo = item.desDestinoImc === 'externo';

  return { rotulo, interno: !externo, link, novaAba: externo && item.bitNovaAbaImc, subitens: [] };
}

function existe<T>(valor: T | null): valor is T {
  return valor !== null;
}

/**
 * O menu da API pronto para o cabeçalho. A API já tira o que o portal não consegue mostrar;
 * por defesa, item sem rótulo, link sem link e dropdown sem subitem também ficam de fora.
 */
export function paraMenuPortal(resposta: readonly ItemMenuPublicoResponse[]): ItemMenuPortal[] {
  return resposta
    .map((item) => {
      const subitens = (item.subitens ?? []).map(paraLink).filter(existe);
      const rotulo = item.desRotuloImc?.trim();

      if (subitens.length === 0) return paraLink(item);

      return rotulo ? { rotulo, interno: true, link: null, novaAba: false, subitens } : null;
    })
    .filter(existe);
}

/**
 * O menu personalizado que o órgão publica no cabeçalho. É raiz: uma requisição por
 * carregamento do portal. O portal não envia o órgão: o back-end o resolve pelo host.
 *
 * `itens` vazio quer dizer "mostre o menu padrão": a API devolveu a lista vazia (origem
 * padrão, órgão sem cabeçalho ou nenhum item ativo) ou falhou. Durante o carregamento,
 * `itens` também é vazio, e o cabeçalho espera por `carregando` para não trocar de menu
 * na frente do visitante.
 */
@Service()
export class MenuCabecalhoPublicoService {
  private readonly menuApi = httpResource<ResponseApi<ItemMenuPublicoResponse[]>>(() => ({
    url: RECURSO,
    context: ERRO_LOCAL(),
  }));

  readonly carregando = computed(() => this.menuApi.isLoading());

  /** O `error()` é consultado antes do `value()`, que lança em estado de erro. */
  readonly itens = computed<readonly ItemMenuPortal[]>(() => {
    if (this.menuApi.error() !== undefined) return [];

    return paraMenuPortal(this.menuApi.value()?.data ?? []);
  });
}
